/**
 * src/core/dom.js
 *
 * Helper DOM 100 % reactivo
 */

import { effect } from '@core/signal.js'

/* ----------  utilidades internas  ---------- */
const isSignal = v => v && typeof v.get === 'function'

/* ----------  1.  bindProp  ---------- */
export const bindProp = (el, prop, signal) => {
    el[prop] = signal.get()
    effect(() => { el[prop] = signal.get() })
    return el
}

/* ----------  2.  bindAttr  ---------- */
export const bindAttr = (el, attr, signal) => {
    const read = () => {
        const v = signal.get()
        v == null ? el.removeAttribute(attr) : el.setAttribute(attr, v)
    }
    read()                       // inicial
    effect(read)                 // reactivo
    return el
}

/* ----------  3.  reactiveText  ---------- */
export const reactiveText = signal => {
    const node = document.createTextNode(signal.get())
    effect(() => { node.nodeValue = signal.get() })
    return node
}

/* ----------  4.  clearElement  ---------- */
export const clearElement = el => { while (el.firstChild) el.removeChild(el.firstChild) }

/* ----------  5.  fragment  ---------- */
export const fragment = (...children) => {
    const f = document.createDocumentFragment()
    children.flat(Infinity).forEach(ch => {
        if (ch == null) return
        f.appendChild(ch instanceof Node ? ch : document.createTextNode(String(ch)))
    })
    return f
}

/* ----------  6.  bind múltiple  ---------- */
export const bind = (el, bindings) => {
    Object.entries(bindings).forEach(([k, signal]) => {
        if (!isSignal(signal)) return
        if (k === 'style')        bindProp(el, 'style', signal)
        else if (k === 'className') bindProp(el, 'className', signal)
        else if (k.startsWith('attr:')) bindAttr(el, k.slice(5), signal)
        else                      bindProp(el, k, signal)
    })
    return el
}

/* ----------  7.  h  –  factory de elementos / componentes  ---------- */
export const h = (tag, attrs = {}, ...children) => {
    /* 7.a  componente función  */
    if (typeof tag === 'function') return tag({ ...attrs, children })

    /* 7.b  elemento normal  */
    const el = document.createElement(tag)

    /* 7.c  atributos / props / eventos  */
    Object.entries(attrs).forEach(([k, v]) => {
        /* eventos  onClick  o  on:click  */
        if (k.startsWith('on')) {
            const event = k.startsWith('on:') ? k.slice(3) : k.slice(2).toLowerCase()
            
            if (isSignal(v)) {
                let currentHandler = v.get()
                if (currentHandler) {
                    el.addEventListener(event, currentHandler)
                }
                
                effect(() => {
                    const newHandler = v.get()
                    if (currentHandler) {
                        el.removeEventListener(event, currentHandler)
                    }
                    if (newHandler) {
                        el.addEventListener(event, newHandler)
                    }
                    currentHandler = newHandler
                })
            } else {
                el.addEventListener(event, v)
            }
            return
        }

        /* LISTA de propiedades DOM que NO son atributos HTML */
        const domProperties = [
            'textContent', 'innerHTML', 'innerText', 'value',
            'checked', 'disabled', 'selected', 'multiple',
            'readOnly', 'required', 'placeholder', 'type',
            'src', 'href', 'alt', 'title', 'tabIndex'
        ]
            
        const specialProperties = ['className', 'style', 'htmlFor']
            
        /* className  –  array | objeto | string | signal  */
        if (k === 'className' || k === 'class') {
            const propName = 'className'
            
            if (isSignal(v)) { 
                bindProp(el, propName, v) 
                return 
            }
            
            if (Array.isArray(v)) {
                const base = v.filter(x => typeof x === 'string')
                const sig  = v.find(isSignal)
                const apply = () => {
                    el[propName] = base.concat(sig ? sig.get() : []).join(' ')
                }
                apply() 
                effect(apply) 
                return
            }
            
            if (typeof v === 'object' && !Array.isArray(v)) {
                const keys = Object.keys(v)
                const apply = () => {
                    const list = keys.filter(cls => {
                        const cond = v[cls]
                        return isSignal(cond) ? cond.get() : Boolean(cond)
                    })
                    el[propName] = list.join(' ')
                }
                apply() 
                effect(apply) 
                return
            }
            
            el[propName] = v || ''
            return
        }

        /* style  –  string | signal | objeto  */
        if (k === 'style') {
            if (isSignal(v)) { 
                bindProp(el, 'style', v) 
                return 
            }
            
            if (typeof v === 'object' && !Array.isArray(v)) {
                Object.entries(v).forEach(([cssProp, val]) => {
                    if (isSignal(val)) {
                        effect(() => el.style[cssProp] = val.get())
                        el.style[cssProp] = val.get()
                    } else {
                        el.style[cssProp] = val
                    }
                })
                return
            }
        
            el.style.cssText = v || ''
            return
        }

        /* htmlFor (especial para <label>) */
        if (k === 'htmlFor') {
            if (isSignal(v)) {
                effect(() => el.setAttribute('for', v.get()))
                el.setAttribute('for', v.get())
            } else {
                el.setAttribute('for', v)
            }
            return
        }

        /* VALORES ESTÁTICOS (no señales) */
        if (!isSignal(v)) {
            if (v == null) return // Ignorar null/undefined
            
            // Propiedades DOM directas
            if (domProperties.includes(k)) {
                el[k] = v
            }
            // className ya se manejó arriba
            else if (k === 'className' || k === 'class') {
                el.className = v
            }
            // Atributos normales
            else {
                el.setAttribute(k, v)
            }
            return 
        }

        /* VALORES COMO SEÑALES */
        // textContent e innerHTML como señales
        if (k === 'textContent' || k === 'innerHTML') {
            bindProp(el, k, v)
        }
        // Propiedades DOM como señales
        else if (domProperties.includes(k)) {
            bindProp(el, k, v)
        }
        // Atributos normales como señales
        else {
            bindAttr(el, k, v)
        }
    })

    /* 7.d  hijos  (aplanados)  */
    if (children.length > 0) {
        // Verificar si ya se estableció textContent o innerHTML
        const hasContentProperty = attrs.textContent !== undefined || 
                                attrs.innerHTML !== undefined
        
        // Solo añadir hijos si no hay textContent/innerHTML
        if (!hasContentProperty) {
            children.flat(Infinity).forEach(ch => {
                if (ch == null) return
                
                if (isSignal(ch)) {
                    el.appendChild(reactiveText(ch))
                } else {
                    el.appendChild(
                        ch instanceof Node ? ch : document.createTextNode(String(ch))
                    )
                }
            })
        }
    }

    return el
}

/* ----------  7.b  –  texto simple  ---------- */
export const text = (content) => {
    if (isSignal(content)) {
        return reactiveText(content)
    }
    return document.createTextNode(String(content))
}

// Uso:
// h('p', { className: 'message' }, text('Texto estático'))
// h('p', { className: 'message' }, text(dynamicSignal))

/* ----------  8.  alias JSX  ---------- */
export const jsx = h